import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import {
  alarmModelQuery,
  resetAlarmModel,
  saveAlarmModel,
  type AlarmModelChoice,
  type AlarmModelOverride,
  type AlarmModelPage,
  type AlarmModelSettings,
} from '../../api/settingsAiAgent'
import { Spinner } from '../../ui/Spinner'
import { failed, NoteText, type Note } from './parts'

const CLAUDE = 'anthropic'

function usd(n: number): string {
  return '$' + n.toFixed(2)
}

/** Eticheta unui model: pretul pe 1M si costul estimat pe alarma, din API. */
function choiceLabel(c: AlarmModelChoice): string {
  if (!c.price) return c.model + ' (no price)'
  const per = c.per_alarm === null ? '' : ', ~$' + c.per_alarm.toFixed(c.per_alarm < 0.01 ? 4 : 3) + ' / alarm'

  return `${c.model} (${usd(c.price.in)} / ${usd(c.price.out)} per 1M${per})`
}

/**
 * Modelele de ales pentru o firma: Claude mereu (e implicitul), celelalte
 * numai daca laboratorul lor e permis in buget. Fara `choices` (API vechi):
 * scara Claude, fara pret.
 */
function companyChoices(page: AlarmModelPage, allowed: string[] | null | undefined): AlarmModelChoice[] {
  const all =
    page.choices ??
    page.models.map((m) => ({ value: m, model: m, lab_id: 0, lab_name: 'Anthropic (Claude)', catalog: CLAUDE, price: null, per_alarm: null }))

  return all.filter((c) => c.catalog === CLAUDE || allowed == null || allowed.includes(c.catalog))
}

/** Optiunile, grupate pe laborator, Claude intai; valoarea curenta nu dispare. */
function ModelOptions({ choices, current }: { choices: AlarmModelChoice[]; current: string }) {
  const groups: { name: string; items: AlarmModelChoice[] }[] = []
  for (const c of choices) {
    const last = groups[groups.length - 1]
    if (last && last.name === c.lab_name) last.items.push(c)
    else groups.push({ name: c.lab_name, items: [c] })
  }
  const missing = current !== '' && !choices.some((c) => c.value === current)

  return (
    <>
      {groups.map((g) => (
        <optgroup key={g.name} label={g.name}>
          {g.items.map((c) => (
            <option key={c.value} value={c.value}>
              {choiceLabel(c)}
            </option>
          ))}
        </optgroup>
      ))}
      {missing ? <option value={current}>{current} (not available)</option> : null}
    </>
  )
}

/**
 * Fila "Alarm model" (Global, numai pentru un administrator de platforma -
 * poarta e pe server, fila doar nu se deseneaza altora).
 *
 * Cu ce model se face diagnosticul AUTOMAT al alarmelor, pe firma. Implicitul
 * e cel mai ieftin: Auto porneste pe Haiku 5.5 si urca la Sonnet 5.5 numai la
 * semne clare (unelte picate de doua ori - o data la o alarma critica -, cauza
 * negasita, o actiune de scriere), iar Opus 5.5 doar daca adminul ridica
 * plafonul. Alarmele critice pornesc tot pe Haiku. Aceeasi
 * alarma diagnosticata in ultimele 24 de ore se refoloseste, fara model.
 *
 * Conversatiile cu oameni nu se schimba de aici.
 */
export function AlarmModelTab() {
  const client = useQueryClient()
  const query = useQuery(alarmModelQuery())
  const page = query.data ?? null
  const [companyId, setCompanyId] = useState(0)

  useEffect(() => {
    if (page && companyId === 0 && page.companies.length > 0) setCompanyId(page.companies[0].id)
  }, [page, companyId])

  if (query.isError) {
    return <p className="text-danger">{failed(query.error)}</p>
  }
  if (!page) return <Spinner />

  const company = page.companies.find((c) => c.id === companyId) ?? null
  const reload = () => {
    void client.invalidateQueries({ queryKey: ['settings', 'ai_agent', 'alarm_model'] })
  }

  return (
    <div className="panel panel-default aia-alarm-model">
      <div className="panel-heading">
        <strong>Alarm model</strong>
        <span className="text-muted" style={{ fontSize: 12, marginLeft: 8 }}>
          the model that diagnoses alarms on its own, per company. Conversations with people are not affected.
        </span>
      </div>
      <div className="panel-body">
        {!page.ready ? (
          <p className="text-danger" style={{ fontSize: 13 }} data-testid="alarm-model-missing">
            {page.missing}
          </p>
        ) : null}

        <div className="form-group" style={{ maxWidth: 420 }}>
          <label htmlFor="aia-alarm-company">Company</label>
          <select
            id="aia-alarm-company"
            className="form-control input-sm"
            value={companyId}
            onChange={(e) => setCompanyId(Number(e.target.value))}
          >
            {page.companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
                {c.custom ? ' (set here)' : ' (default)'}
              </option>
            ))}
          </select>
        </div>

        {company ? (
          <CompanyForm
            key={company.id + (company.custom ? ':c' : ':d')}
            page={page}
            companyId={company.id}
            custom={company.custom}
            initial={company.settings}
            choices={companyChoices(page, company.allowedLabs)}
            onSaved={reload}
          />
        ) : (
          <p className="text-muted">No company.</p>
        )}
      </div>
    </div>
  )
}

function CompanyForm({
  page,
  companyId,
  custom,
  initial,
  choices,
  onSaved,
}: {
  page: AlarmModelPage
  companyId: number
  custom: boolean
  initial: AlarmModelSettings
  choices: AlarmModelChoice[]
  onSaved(): void
}) {
  const [s, setS] = useState<AlarmModelSettings>(initial)
  const [note, setNote] = useState<Note | null>(null)
  const disabled = !page.ready
  /* Plafonul ramane pe scara Claude; pornirea, Fixed si exceptiile Fixed pot fi orice model. */
  const claude = choices.filter((c) => c.catalog === CLAUDE)
  const start = s.start ?? page.models[0]

  const save = () => {
    setNote({ text: '…' })
    void saveAlarmModel(companyId, s)
      .then((r) => {
        if (!r.ok) {
          setNote({ text: r.error ?? 'Failed', bad: true })
          return
        }
        setNote({ text: 'Saved' })
        onSaved()
      })
      .catch((e: unknown) => setNote({ text: failed(e), bad: true }))
  }

  const reset = () => {
    setNote({ text: '…' })
    void resetAlarmModel(companyId)
      .then((r) => {
        if (!r.ok) {
          setNote({ text: r.error ?? 'Failed', bad: true })
          return
        }
        setS(page.defaults)
        setNote({ text: 'Back to the default' })
        onSaved()
      })
      .catch((e: unknown) => setNote({ text: failed(e), bad: true }))
  }

  const setOverride = (i: number, o: AlarmModelOverride | null) => {
    const next = [...s.overrides]
    if (o === null) next.splice(i, 1)
    else next[i] = o
    setS({ ...s, overrides: next })
  }

  return (
    <div style={{ fontSize: 13 }}>
      <div className="radio">
        <label>
          <input
            type="radio"
            name="aia-alarm-mode"
            checked={s.mode === 'auto'}
            disabled={disabled}
            onChange={() => setS({ ...s, mode: 'auto' })}
          />{' '}
          <strong>Auto</strong> - start on {start}, move up only on clear signs (tools failed twice - once on a
          critical alarm -, cause not found or not sure, a write action next), keeping what was found.
        </label>
      </div>
      {s.mode === 'auto' ? (
        <div style={{ marginLeft: 20, maxWidth: 480 }}>
          <div className="form-group">
            <label htmlFor="aia-alarm-start">Start on</label>
            <select
              id="aia-alarm-start"
              className="form-control input-sm"
              value={start}
              disabled={disabled}
              onChange={(e) => setS({ ...s, start: e.target.value })}
            >
              <ModelOptions choices={choices} current={start} />
            </select>
            {!claude.some((c) => c.value === start) ? (
              <span className="help-block" style={{ fontSize: 12 }}>
                A model from another lab runs on that lab. It moves up to the Claude models below only if the company
                allows Anthropic; otherwise it stays.
              </span>
            ) : null}
          </div>
          <div className="form-group">
            <label htmlFor="aia-alarm-ceiling">Highest model it may move up to</label>
            <select
              id="aia-alarm-ceiling"
              className="form-control input-sm"
              value={s.ceiling}
              disabled={disabled}
              onChange={(e) => setS({ ...s, ceiling: e.target.value })}
            >
              <ModelOptions choices={claude} current={s.ceiling} />
            </select>
          </div>
        </div>
      ) : null}
      <div className="radio">
        <label>
          <input
            type="radio"
            name="aia-alarm-mode"
            checked={s.mode === 'fixed'}
            disabled={disabled}
            onChange={() => setS({ ...s, mode: 'fixed' })}
          />{' '}
          <strong>Fixed</strong> - always the same model
        </label>
      </div>
      {s.mode === 'fixed' ? (
        <div className="form-group" style={{ marginLeft: 20, maxWidth: 480 }}>
          <label htmlFor="aia-alarm-fixed">Model</label>
          <select
            id="aia-alarm-fixed"
            className="form-control input-sm"
            value={s.fixed_model}
            disabled={disabled}
            onChange={(e) => setS({ ...s, fixed_model: e.target.value })}
          >
            <ModelOptions choices={choices} current={s.fixed_model} />
          </select>
        </div>
      ) : null}

      <div className="checkbox" style={{ marginTop: 12 }}>
        <label>
          <input
            type="checkbox"
            checked={s.dedup}
            disabled={disabled}
            onChange={(e) => setS({ ...s, dedup: e.target.checked })}
          />{' '}
          Reuse the last diagnosis of the same alarm (same type, same machine) for
        </label>{' '}
        <input
          type="number"
          aria-label="Reuse window in hours"
          min={1}
          max={page.maxHours}
          value={s.dedup_hours}
          disabled={disabled || !s.dedup}
          style={{ width: 64, display: 'inline-block' }}
          className="form-control input-sm"
          onChange={(e) => setS({ ...s, dedup_hours: Number(e.target.value) || page.defaults.dedup_hours })}
        />{' '}
        hours, without asking the model again.
      </div>

      <div className="checkbox">
        <label>
          <input
            type="checkbox"
            checked={s.fleet !== false}
            disabled={disabled}
            onChange={(e) => setS({ ...s, fleet: e.target.checked })}
          />{' '}
          Reuse the diagnosis of the same alarm on another machine (same type, same service), within the same window -
          marked &quot;same diagnosis as on &lt;machine&gt;&quot;. Only when diagnosing; alarms with a measured value
          (disk, RAM) always run on their own machine.
        </label>
      </div>

      <div className="form-group" style={{ marginTop: 12, maxWidth: 520 }}>
        <label htmlFor="aia-alarm-noise">Known noise - never sent to the model</label>
        <textarea
          id="aia-alarm-noise"
          className="form-control input-sm"
          rows={4}
          value={(s.noise ?? []).join('\n')}
          disabled={disabled}
          onChange={(e) =>
            setS({ ...s, noise: e.target.value.split('\n').slice(0, page.maxNoise) })
          }
        />
        <span className="help-block" style={{ fontSize: 12 }}>
          One pattern per line, on the alarm type or the service name; <code>*</code> matches anything (e.g.{' '}
          <code>GoogleUpdaterService*</code>). A matching alarm is recorded as skipped, with the pattern as the reason.
        </span>
      </div>

      <h5 style={{ marginTop: 16 }}>Exceptions by alarm type (optional)</h5>
      <table className="table table-condensed" style={{ fontSize: 12, maxWidth: 960 }}>
        <thead>
          <tr>
            <th>Alarm type</th>
            <th>Mode</th>
            <th>Model (highest, or fixed)</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {s.overrides.map((o, i) => (
            <tr key={i}>
              <td>
                <input
                  className="form-control input-sm"
                  aria-label="Alarm type"
                  list="aia-alarm-types"
                  value={o.kind}
                  disabled={disabled}
                  onChange={(e) => setOverride(i, { ...o, kind: e.target.value })}
                />
              </td>
              <td>
                <select
                  className="form-control input-sm"
                  aria-label="Exception mode"
                  value={o.mode}
                  disabled={disabled}
                  onChange={(e) => {
                    const mode = e.target.value === 'fixed' ? 'fixed' : 'auto'
                    /* La Auto modelul e plafonul: numai Claude. */
                    const model = mode === 'auto' && !claude.some((c) => c.value === o.model) ? page.defaults.ceiling : o.model
                    setOverride(i, { ...o, mode, model })
                  }}
                >
                  <option value="auto">Auto</option>
                  <option value="fixed">Fixed</option>
                </select>
              </td>
              <td>
                <select
                  className="form-control input-sm"
                  aria-label="Exception model"
                  value={o.model}
                  disabled={disabled}
                  onChange={(e) => setOverride(i, { ...o, model: e.target.value })}
                >
                  <ModelOptions choices={o.mode === 'fixed' ? choices : claude} current={o.model} />
                </select>
              </td>
              <td>
                <button type="button" className="btn btn-link btn-xs" disabled={disabled} onClick={() => setOverride(i, null)}>
                  remove
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <datalist id="aia-alarm-types">
        {page.alarmTypes.map((t) => (
          <option key={t.id} value={t.name} />
        ))}
      </datalist>
      <button
        type="button"
        className="btn btn-default btn-xs"
        disabled={disabled}
        onClick={() =>
          setS({ ...s, overrides: [...s.overrides, { kind: '', mode: 'auto', model: page.defaults.ceiling }] })
        }
      >
        Add exception
      </button>

      <div style={{ marginTop: 16 }}>
        <button type="button" className="btn btn-primary btn-sm" disabled={disabled} onClick={save}>
          Save
        </button>{' '}
        {custom ? (
          <button type="button" className="btn btn-default btn-sm" disabled={disabled} onClick={reset}>
            Reset to default
          </button>
        ) : (
          <span className="text-muted">This company runs on the default.</span>
        )}{' '}
        <NoteText note={note} />
      </div>
    </div>
  )
}
